import { Envelope, ArrowUpRight, Buildings } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { PageLayout } from "@/components/layout";

import gokayPhoto from "@/assets/images/team/gokay-saldamli.png";
import jacksonPhoto from "@/assets/images/team/jackson-blazensky.png";
import alexisPhoto from "@/assets/images/team/alexis-asseman.png";
import onurPhoto from "@/assets/images/team/onur-soysal.png";
import gustavoPhoto from "@/assets/images/team/gustavo-inacio.png";
import carlosPhoto from "@/assets/images/team/carlos-vargas.png";
import bryanPhoto from "@/assets/images/team/bryan-cole.png";
import nelsPhoto from "@/assets/images/team/nels-chantarotwong.png";
import shahoodPhoto from "@/assets/images/team/shahood-khan.png";
import jordanPhoto from "@/assets/images/team/jordan-rein.png";
import tarangPhoto from "@/assets/images/team/tarang-gala.png";
import johnPhoto from "@/assets/images/team/john-corrigan.png";

type CropStyle = { width: string; height: string; left: string; top: string };

const founders: { name: string; role: string; bio: string; photo: string; crop: CropStyle }[] = [
	{
		name: "Gokay Saldamli",
		role: "Co-Founder & CEO",
		bio: "Distributed systems, applied cryptography. Formerly engineering at The Graph.",
		photo: gokayPhoto,
		crop: { width: "100%", height: "142.75%", left: "-0.73%", top: "-5.29%" },
	},
	{
		name: "Jackson Blazensky",
		role: "Co-Founder & CTO",
		bio: "Network protocols, systems architecture. Formerly engineering at The Graph.",
		photo: jacksonPhoto,
		crop: { width: "297.19%", height: "396.25%", left: "-114.22%", top: "-144.38%" },
	},
];

const team: { name: string; role: string; photo: string; crop?: CropStyle; pos?: string }[] = [
	{ name: "Alexis Asseman", role: "Engineering Lead", photo: alexisPhoto, crop: { width: "123.75%", height: "165%", left: "-7.57%", top: "-17.51%" } },
	{ name: "Onur Soysal", role: "Engineering", photo: onurPhoto, crop: { width: "100%", height: "145.87%", left: "0%", top: "-36.46%" } },
	{ name: "Gustavo Inacio", role: "Tech Lead", photo: gustavoPhoto, crop: { width: "127.38%", height: "190.84%", left: "-12.09%", top: "-23.86%" } },
	{ name: "Carlos Vargas", role: "Engineering", photo: carlosPhoto, crop: { width: "100%", height: "102.57%", left: "0%", top: "-1.29%" } },
	{ name: "Bryan Cole", role: "Engineering", photo: bryanPhoto },
	{ name: "Nels C.", role: "QA Testing", photo: nelsPhoto },
	{ name: "Shahood Khan", role: "Engineering", photo: shahoodPhoto, crop: { width: "114.51%", height: "214.22%", left: "-15.36%", top: "-60.36%" } },
	{ name: "Jordan Rein", role: "Product Lead", photo: jordanPhoto, crop: { width: "278%", height: "187%", left: "-86%", top: "-36%" } },
	{ name: "Tarang Gala", role: "Design Lead", photo: tarangPhoto },
	{ name: "John Corrigan", role: "Product", photo: johnPhoto, crop: { width: "280%", height: "187%", left: "-90%", top: "-37%" } },
];

export default function AboutRoute() {
	return (
		<PageLayout activeRoute="/about">
			{/* Hero */}
			<section className="pt-16 md:pt-24 pb-12 md:pb-16 px-9 md:px-[8%] lg:px-[15%]">
				<div className="max-w-2xl mx-auto text-center">
					<motion.h1
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="font-sans text-[28px] md:text-[32px] lg:text-[42px] font-normal tracking-tight text-white"
					>
						About Basilica
					</motion.h1>
					<motion.p
						initial={{ opacity: 0, y: 15 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.35 }}
						className="mt-5 md:mt-6 font-sans text-[17px] md:text-xl leading-relaxed text-white/50"
					>
						Basilica grew out of original research in cryptographic verification, graph theory, and distributed consensus. We founded the company to apply those results to real infrastructure: correct by construction, formally verifiable, and designed to operate at&nbsp;scale.
					</motion.p>
				</div>
			</section>

{/* Team */}
			<section className="px-9 md:px-[8%] lg:px-[15%] py-16 md:py-24">
				<motion.h2
					initial={{ opacity: 0, y: 12 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
					className="font-sans text-[28px] md:text-[32px] lg:text-[42px] font-normal tracking-tight text-white text-center mb-12 md:mb-14"
				>
					Team
				</motion.h2>

				{/* Founders — centered row */}
				<div className="flex justify-center gap-12 md:gap-20 mb-14 md:mb-16">
					{founders.map((founder, i) => (
						<motion.div
							key={founder.name}
							initial={{ opacity: 0, y: 16 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.4, delay: i * 0.1, ease: [0.25, 1, 0.5, 1] }}
							className="group flex flex-col items-center text-center"
						>
							<div className="relative w-[96px] h-[96px] mb-3.5 rounded-full overflow-hidden ring-1 ring-white/[0.08] group-hover:ring-white/[0.16] transition-all duration-300">
								<img
									src={founder.photo}
									alt={founder.name}
									className="absolute max-w-none grayscale group-hover:grayscale-0 transition-[filter] duration-500"
									style={founder.crop}
								/>
							</div>
							<h3 className="text-[15px] text-white font-normal tracking-tight">{founder.name}</h3>
							<p className="text-[14px] text-white/30 mt-0.5">{founder.role}</p>
						</motion.div>
					))}
				</div>

				{/* Team members — 5-col grid, 2 rows of 5 */}
				<div className="grid grid-cols-3 sm:grid-cols-5 gap-x-20 gap-y-12 max-w-[960px] mx-auto">
					{team.map((person, i) => (
						<motion.div
							key={person.name}
							initial={{ opacity: 0, y: 12 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.3, delay: i * 0.03, ease: [0.25, 1, 0.5, 1] }}
							className="group flex flex-col items-center text-center"
						>
							<div className="relative w-[96px] h-[96px] mb-3.5 rounded-full overflow-hidden ring-1 ring-white/[0.06] group-hover:ring-white/[0.14] transition-all duration-300">
								<img
									src={person.photo}
									alt={person.name}
									className={`grayscale group-hover:grayscale-0 transition-[filter] duration-500 ${person.crop ? "absolute max-w-none" : "w-full h-full object-cover"}`}
									style={person.crop ?? (person.pos ? { objectPosition: person.pos } : undefined)}
								/>
							</div>
							<h3 className="text-[15px] text-white font-normal tracking-tight">{person.name}</h3>
							<p className="text-[14px] text-white/30 mt-0.5">{person.role}</p>
						</motion.div>
					))}
				</div>
			</section>

{/* Office */}
			<section className="px-9 md:px-[8%] lg:px-[15%] py-16 md:py-24">
				<div className="grid md:grid-cols-[1fr_320px] gap-10 md:gap-12">
					<motion.div
						initial={{ opacity: 0, y: 16 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
						className="relative rounded-2xl overflow-hidden border border-white/[0.06] aspect-[16/9] md:aspect-auto md:min-h-[360px]"
					>
						<iframe
							title="Basilica office location"
							src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3189.5!2d-122.0147!3d37.0511!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808e4a1b0e68c7e7%3A0x0!2s100+Enterprise+Way%2C+Scotts+Valley%2C+CA+95066!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
							className="absolute inset-0 w-full h-full border-0 grayscale invert opacity-80"
							loading="lazy"
							referrerPolicy="no-referrer-when-downgrade"
						/>
					</motion.div>
					<motion.div
						initial={{ opacity: 0, y: 16 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.1, ease: [0.25, 1, 0.5, 1] }}
						className="flex flex-col justify-between"
					>
						<div>
							<motion.h2
								initial={{ opacity: 0, y: 12 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
								className="font-sans text-[28px] md:text-[32px] lg:text-[42px] font-normal tracking-tight text-white mb-6"
							>
								Office
							</motion.h2>
							<p className="text-[16px] text-white/40 leading-relaxed mb-8">
								Our team works from the Santa Cruz mountains and Austin, TX.
							</p>

							<div className="space-y-4">
								<div className="flex items-start gap-3 text-white/40">
									<Buildings size={18} className="mt-0.5 shrink-0" />
									<div>
										<p className="text-[15px] text-white/60">Basilica Digital Inc.</p>
										<p className="text-[14px] text-white/35 mt-0.5">100 Enterprise Way<br />Scotts Valley, CA 95066</p>
									</div>
								</div>
								<a
									href="mailto:contact@basilica.digital"
									className="group flex items-center gap-3 text-white/40 hover:text-white transition-colors duration-200"
								>
									<Envelope size={18} className="shrink-0" />
									<span className="text-[15px]">contact@basilica.digital</span>
									<ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200" />
								</a>
							</div>
						</div>
					</motion.div>
				</div>
			</section>
		</PageLayout>
	);
}
